import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import * as Sentry from "@sentry/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const authClient = axios.create({ baseURL: API, withCredentials: true });

// Tag every Sentry event with the current user so production crashes are
// immediately associated with the affected account. Cleared on logout.
function syncSentryUser(u) {
  try {
    if (u && u.id) {
      Sentry.setUser({ id: u.id, email: u.email, username: u.name });
    } else {
      Sentry.setUser(null);
    }
  } catch {
    /* never let Sentry break auth */
  }
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null); // null = loading, false = not authed, object = authed
  const [loading, setLoading] = useState(true);

  // Wrap setUser so every state change also re-syncs the Sentry identity.
  const setUser = (u) => {
    syncSentryUser(u && typeof u === "object" ? u : null);
    setUserState(u);
  };

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /users/me
    // check. AuthCallback will exchange the session_id and establish the JWT
    // cookie first; running /users/me now would just hit a 401 and flash the
    // logged-out UI for a few hundred ms.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await authClient.get("/users/me");
        setUser(data);
      } catch { setUser(false); }
      setLoading(false);
    })();
  }, []);

  const register = async ({ email, password, name, institution, privacy_consent }) => {
    const { data } = await authClient.post("/users/register", { email, password, name, institution, privacy_consent });
    // Re-fetch via /users/me so the panel sees freshly-derived fields like
    // public_slug that aren't part of the register response.
    try {
      const me = await authClient.get("/users/me");
      setUser(me.data);
    } catch {
      setUser(data);
    }
    return data;
  };

  const login = async ({ email, password }) => {
    const { data } = await authClient.post("/users/login", { email, password });
    try {
      const me = await authClient.get("/users/me");
      setUser(me.data);
    } catch {
      setUser(data);
    }
    return data;
  };

  const logout = async () => {
    try { await authClient.post("/users/logout", {}); } catch { /* ignore */ }
    setUser(false);
  };

  // Iter307.3 — Troca de senha do usuário regular (db.users). Usado no
  // fluxo forçado após admin gerar senha provisória (must_change_password)
  // e também para troca voluntária a partir do perfil.
  const changePassword = async ({ current_password, new_password, confirm_password }) => {
    const { data } = await authClient.post("/users/change-password", {
      current_password,
      new_password,
      confirm_password,
    });
    // Refresh do /users/me para limpar a flag localmente sem depender de
    // um re-login manual.
    try {
      const me = await authClient.get("/users/me");
      setUser(me.data);
    } catch { /* mantém estado atual */ }
    return data;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Direct Google OAuth 2.0 — uses our own Google Cloud OAuth client so
  // the consent screen reads "Continuar para JurisTube". Requires the
  // Google Cloud project to be properly configured AND propagated (can
  // take up to 24h after a config change). When this path fails the user
  // gets a `?google_error=…` redirect back to /conta with a clear toast.
  const startGoogleLogin = (redirectPath = "/minhas-colecoes") => {
    const next = encodeURIComponent(redirectPath || "/minhas-colecoes");
    window.location.href = `/api/auth/google/login?next=${next}`;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Fallback: Emergent-managed Google auth (auth.emergentagent.com).
  // Shows the "emergentagent.com" branding on the consent screen, but
  // it just works — no Google Cloud project to maintain, no verification
  // workflow. Kept as the primary option until the direct OAuth flow
  // above is fully propagated and verified by Google.
  const startEmergentLogin = (redirectPath = "/minhas-colecoes") => {
    const redirectUrl = window.location.origin + redirectPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Exchange the Emergent session_id (received in the URL fragment after the
  // Google callback) for our own JWT cookies. On success the server sets the
  // access_token/refresh_token cookies; we then refetch /users/me so the
  // AuthContext is populated with the full derived profile.
  const completeGoogleLogin = async (session_id) => {
    const { data } = await authClient.post("/auth/google/session", { session_id });
    try {
      const me = await authClient.get("/users/me");
      setUser(me.data);
    } catch {
      setUser(data);
    }
    return data;
  };

  const deleteAccount = async () => {
    await authClient.delete("/users/me");
    setUser(false);
  };

  const exportMyData = () => authClient.get("/users/me/data").then((r) => r.data);

  const updateProfile = async (body) => {
    const { data } = await authClient.put("/users/me", body);
    setUser(data);
    return data;
  };

  const updateAvatar = async (avatar) => {
    const { data } = await authClient.put("/users/me/avatar", { avatar });
    setUser((prev) => prev && prev !== false ? { ...prev, avatar: data.avatar } : prev);
    return data;
  };

  const comments = {
    list: (episodeId) => authClient.get(`/episodes/${episodeId}/comments`).then((r) => r.data),
    create: (episodeId, message) =>
      authClient.post(`/episodes/${episodeId}/comments`, { episode_id: episodeId, message }).then((r) => r.data),
    remove: (commentId) => authClient.delete(`/comments/${commentId}`).then((r) => r.data),
    userHistory: (userId) => authClient.get(`/admin/users/${userId}/comments`).then((r) => r.data),
    setBan: (userId, banned, reason = "") =>
      authClient.post(`/admin/users/ban`, { user_id: userId, banned, reason }).then((r) => r.data),
  };

  const forgotPassword = (email) =>
    authClient.post("/users/forgot-password", { email }).then((r) => r.data);

  const resetPassword = async (token, new_password) => {
    const { data } = await authClient.post("/users/reset-password", { token, new_password });
    setUser({ id: data.id, email: data.email, name: data.name, institution: data.institution });
    return data;
  };

  // Request a fresh email-verification link. Rate-limited server-side (3
  // calls / hour). Returns {ok, already_verified} so the UI can show the
  // right toast without branching on the error channel.
  const resendVerification = () =>
    authClient.post("/users/me/resend-verification").then((r) => r.data);

  const collections = {
    list: () => authClient.get("/collections").then((r) => r.data),
    get: (id) => authClient.get(`/collections/${id}`).then((r) => r.data),
    create: (body) => authClient.post("/collections", body).then((r) => r.data),
    update: (id, body) => authClient.put(`/collections/${id}`, body).then((r) => r.data),
    remove: (id) => authClient.delete(`/collections/${id}`).then((r) => r.data),
    getShare: (slug) => authClient.get(`/share/${slug}`).then((r) => r.data),
    addEpisode: (id, epId) => authClient.post(`/collections/${id}/episodes/${epId}`).then((r) => r.data),
    removeEpisode: (id, epId) => authClient.delete(`/collections/${id}/episodes/${epId}`).then((r) => r.data),
    subscribe: (slug) => authClient.post(`/share/${slug}/subscribe`).then((r) => r.data),
    unsubscribe: (slug) => authClient.delete(`/share/${slug}/subscribe`).then((r) => r.data),
    listSubscribed: () => authClient.get("/my/subscribed-collections").then((r) => r.data),
  };

  const newsletter = {
    status: () => authClient.get("/newsletter/my-status").then((r) => r.data),
    toggle: () => authClient.post("/newsletter/my-status").then((r) => r.data),
  };

  // Ecossistema do Aluno (Fase A) — pastas de estudo do próprio usuário.
  // Endpoints scoped ao usuário autenticado (cookie HttpOnly).
  const cadernos = {
    list: () => authClient.get("/cadernos").then((r) => r.data.items || []),
    create: (body) => authClient.post("/cadernos", body).then((r) => r.data),
    update: (id, body) => authClient.put(`/cadernos/${id}`, body).then((r) => r.data),
    remove: (id) => authClient.delete(`/cadernos/${id}`).then((r) => r.data),
    save: (body) => authClient.post("/cadernos/save", body).then((r) => r.data),
    items: (params = {}) => authClient.get("/cadernos/items", { params }).then((r) => r.data),
    removeItem: (id) => authClient.delete(`/cadernos/items/${id}`).then((r) => r.data),
    // Caderno de Estudos (Fev/2026) — anotações + recomendações por tags.
    updateNotes: (id, notes) =>
      authClient.put(`/cadernos/items/${id}/notes`, { notes }).then((r) => r.data),
    related: (id, limit = 3) =>
      authClient.get(`/cadernos/items/${id}/related`, { params: { limit } }).then((r) => r.data),
    check: (target_type, target_id) =>
      authClient.get("/cadernos/check", { params: { target_type, target_id } }).then((r) => r.data),
    // Fase B — compartilhamento + atalho semântico "Meus Professores".
    publish: (id) => authClient.post(`/cadernos/${id}/publish`).then((r) => r.data),
    unpublish: (id) => authClient.delete(`/cadernos/${id}/publish`).then((r) => r.data),
    professors: () => authClient.get("/cadernos/professors").then((r) => r.data),
    // Fase C — reordenação. ``id='null'`` para Minha Biblioteca.
    reorder: (id, item_ids) =>
      authClient.put(`/cadernos/${id}/reorder`, { item_ids }).then((r) => r.data),
    // Público — sem auth.
    getPublic: (token) => authClient.get(`/cadernos/public/${token}`).then((r) => r.data),
  };

  return (
    <AuthContext.Provider value={{ user, loading, register, login, logout, changePassword, deleteAccount, exportMyData, updateProfile, updateAvatar, comments, collections, cadernos, newsletter, forgotPassword, resetPassword, resendVerification, setUser, startGoogleLogin, startEmergentLogin, completeGoogleLogin, authClient }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
